import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/apiConfig';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, MessageSquare, User, Bot, Clock, Phone, ChevronRight, ArrowLeft,
  RefreshCw, UserCog, Send, Loader2, ShieldCheck,
} from 'lucide-react';

export default function WhatsAppConversationsPage() {
  const { admin } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedConv, setSelectedConv] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [ownerBusy, setOwnerBusy] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const msgEndRef = useRef(null);

  useEffect(() => { fetchConversations(); }, [statusFilter]);

  useEffect(() => {
    if (selectedConv && msgEndRef.current) {
      msgEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConv]);

  // Deep-link support: /whatsapp-conversations?conv_id=<id> auto-opens the chat
  useEffect(() => {
    const convId = searchParams.get('conv_id');
    if (convId && conversations.length > 0 && !selectedConv) {
      const found = conversations.find((c) => c.id === convId);
      if (found) setSelectedConv(found);
    }
  }, [searchParams, conversations, selectedConv]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await axios.get(`${API}/wa-conversations`, { params });
      setConversations(data.conversations || []);
      if (selectedConv) {
        const refreshed = (data.conversations || []).find((c) => c.id === selectedConv.id);
        if (refreshed) setSelectedConv(refreshed);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatMsgTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      expired: 'bg-slate-100 text-slate-600',
      completed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-600',
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  const getDisplayName = (conv) => conv.contact_name || null;

  const filtered = conversations.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const msgs = c.messages || [];
    return c.mobile.includes(q) || (c.contact_name || '').toLowerCase().includes(q) || msgs.some((m) => m.text?.toLowerCase().includes(q));
  });

  const getLastMessage = (conv) => {
    const msgs = conv.messages || [];
    if (msgs.length === 0) return 'No messages';
    const last = msgs[msgs.length - 1];
    return last.text?.substring(0, 80) + (last.text?.length > 80 ? '...' : '');
  };

  const handleTakeover = async (conv) => {
    setOwnerBusy(true);
    try {
      const { data } = await axios.post(`${API}/webhook/whatsapp/conversations/${conv.id}/takeover`);
      toast.success('You are now handling this conversation — MAKSIO will stay silent', { position: 'bottom-center' });
      const updated = { ...conv, owner_mode: 'human', owner_staff: data.owner_staff };
      setSelectedConv(updated);
      fetchConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to take over conversation');
    } finally {
      setOwnerBusy(false);
    }
  };

  const handleRelease = async (conv) => {
    setOwnerBusy(true);
    try {
      await axios.post(`${API}/webhook/whatsapp/conversations/${conv.id}/release`);
      toast.success('Returned to MAKSIO — AI will resume handling new messages', { position: 'bottom-center' });
      const updated = { ...conv, owner_mode: 'ai', owner_staff: null };
      setSelectedConv(updated);
      fetchConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to return conversation to MAKSIO');
    } finally {
      setOwnerBusy(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedConv) return;
    setSendingReply(true);
    try {
      const { data } = await axios.post(`${API}/webhook/whatsapp/conversations/${selectedConv.id}/reply`, {
        message: replyText.trim(),
      });
      setReplyText('');
      toast.success('Message sent');
      setSelectedConv((prev) => ({
        ...prev,
        owner_mode: data.owner_mode,
        owner_staff: data.owner_staff,
        messages: [...(prev.messages || []), { role: 'staff', text: replyText.trim(), staff_name: data.owner_staff?.name, at: new Date().toISOString() }],
      }));
      fetchConversations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingReply(false);
    }
  };

  const OwnerBadge = ({ conv }) => {
    if (conv.owner_mode === 'human') {
      return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-1" data-testid={`owner-badge-${conv.id}`}>
          <UserCog className="w-3 h-3" /> Human Active{conv.owner_staff?.name ? ` · ${conv.owner_staff.name}` : ''}
        </span>
      );
    }
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700 inline-flex items-center gap-1" data-testid={`owner-badge-${conv.id}`}>
        <Bot className="w-3 h-3" /> AI Active
      </span>
    );
  };

  // Chat detail view
  if (selectedConv) {
    const msgs = selectedConv.messages || [];
    const isHuman = selectedConv.owner_mode === 'human';
    const isMine = isHuman && selectedConv.owner_staff?.email === admin?.email;
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto" data-testid="wa-conv-detail">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => setSelectedConv(null)} data-testid="conv-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-green-700" />
              </div>
              <div className="min-w-0">
                <h2 className="font-semibold text-slate-900 truncate">
                  {getDisplayName(selectedConv) || `+${selectedConv.mobile}`}
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  {getDisplayName(selectedConv) && <span className="text-xs text-slate-400">+{selectedConv.mobile}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(selectedConv.status)}`}>
                    {selectedConv.status}
                  </span>
                  <OwnerBadge conv={selectedConv} />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {isHuman ? (
                <Button size="sm" variant="outline" onClick={() => handleRelease(selectedConv)} disabled={ownerBusy} data-testid="return-to-maksio-btn">
                  {ownerBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bot className="w-4 h-4 mr-1" />}
                  Return to MAKSIO
                </Button>
              ) : (
                <Button size="sm" onClick={() => handleTakeover(selectedConv)} disabled={ownerBusy} data-testid="take-over-chat-btn">
                  {ownerBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserCog className="w-4 h-4 mr-1" />}
                  Take Over Chat
                </Button>
              )}
            </div>
          </div>

          <Card className="overflow-hidden">
            <div className="bg-gradient-to-b from-slate-50 to-white p-4 max-h-[55vh] overflow-y-auto space-y-2" data-testid="conv-messages">
              {msgs.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-green-100 text-green-900 rounded-br-md'
                      : msg.role === 'staff'
                        ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-bl-md shadow-sm'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm'
                  }`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      {msg.role === 'user'
                        ? <User className="w-3 h-3 text-green-600" />
                        : msg.role === 'staff'
                          ? <ShieldCheck className="w-3 h-3 text-amber-600" />
                          : <Bot className="w-3 h-3 text-slate-500" />
                      }
                      <span className="text-[10px] font-medium text-slate-400">
                        {msg.role === 'user' ? 'Player' : msg.role === 'staff' ? (msg.staff_name || 'Staff') : 'Maksio'} · {formatMsgTime(msg.at)}
                      </span>
                    </div>
                    <div className="whitespace-pre-wrap break-words">{msg.text}</div>
                  </div>
                </div>
              ))}
              <div ref={msgEndRef} />
            </div>

            <div className="border-t border-slate-200 p-3 bg-white">
              {isHuman && !isMine && (
                <p className="text-xs text-amber-600 mb-2" data-testid="owned-by-other-warning">
                  Handled by {selectedConv.owner_staff?.name || 'another staff member'} — replying will keep you as the sender.
                </p>
              )}
              <div className="flex gap-2">
                <Textarea
                  rows={2}
                  placeholder="Reply directly to the player over WhatsApp..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  data-testid="staff-reply-input"
                  className="resize-none"
                />
                <Button onClick={handleSendReply} disabled={sendingReply || !replyText.trim()} data-testid="staff-reply-send-btn">
                  {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Sending a reply automatically takes over the chat if MAKSIO is still active.</p>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Conversation list view
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="wa-conversations-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">WhatsApp Conversations</h1>
            <p className="text-sm text-slate-500">All player conversations with Maksio AI</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchConversations} data-testid="conv-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, mobile or message content..."
              className="pl-10"
              data-testid="conv-search-input"
            />
          </div>
          <div className="flex gap-1">
            {['all', 'active', 'expired', 'completed'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  statusFilter === s ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`conv-filter-${s}`}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No conversations found</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((conv) => {
              const msgs = conv.messages || [];
              const displayName = getDisplayName(conv);
              return (
                <Card
                  key={conv.id}
                  className="p-4 cursor-pointer hover:bg-slate-50 transition-colors group"
                  onClick={() => setSelectedConv(conv)}
                  data-testid={`conv-row-${conv.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-semibold text-slate-900 text-sm">
                          {displayName || `+${conv.mobile}`}
                        </span>
                        {displayName && <span className="text-xs text-slate-400">+{conv.mobile}</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getStatusColor(conv.status)}`}>
                          {conv.status}
                        </span>
                        <OwnerBadge conv={conv} />
                      </div>
                      <p className="text-xs text-slate-500 truncate">{getLastMessage(conv)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-center gap-1 text-xs text-slate-400 mb-0.5">
                        <Clock className="w-3 h-3" />
                        {formatTime(conv.updated_at)}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <MessageSquare className="w-3 h-3" />
                        {msgs.length} msgs
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
